import { createSelector } from 'reselect';
import createCachedSelector from 're-reselect';
import { keys, toLower } from 'lodash';
import {
  actionTypes as actionTypeHelpers,
  metrics as metricsHelpers,
  params as paramConstants,
  pageTypes,
  scenarios as scenarioHelpers,
  utils
} from '../helpers';
import * as actionItemHelpers from '../helpers/actionItem';
import {
  buildsReducer,
  dispatchersReducer,
  jobBuildsReducer,
  jobLabelsReducer,
  jobRelationshipReducer,
  jobsReducer,
  metricPageReducer,
  metricsReducer,
  paramsReducer,
  scenarioReducer,
} from '../reducers';
import {
  addOutlierClick,
  removeOutlierClick
} from '../actions';

export const getMetricsOrParamsActions = createSelector(
  paramsReducer.paramsByJobIds,
  jobLabelsReducer.jobLabelsByJobId,
  jobBuildsReducer.buildIdsByJobId,
  buildsReducer.buildsById,
  metricPageReducer.getMetricsPageType,
  jobRelationshipReducer.groupJobRelationshipsByParentJobId,
  scenarioReducer.groupScenariosByRunner,
  scenarioReducer.groupScenariosByDispatcherId,
  jobsReducer.jobsById,
  dispatchersReducer.dispatchersById,
  (
    paramsByIds,
    jobLabelsByJobId,
    buildIdsByJobId,
    buildsById,
    pageType,
    jobRelationshipsByParentJobId,
    scenariosByRunner,
    scenariosByDispatcherId,
    jobsById,
    dispatchersById
  ) =>
    ({
      actionNames, jobId, prevIefVersion,
      prevProcessVersion, hasNcfDiff, hasCcfDiff
    }) =>
    {
      const finalActions = [];
      const params = jobId ? paramsByIds[jobId] : {};
      if (keys(params).length === 0) {
        return [];
      }

      // add previous product versions as additional params
      if (pageType === pageTypes.CASEMETRICS &&
        prevIefVersion && prevProcessVersion) {
        params[paramConstants.PREV_IEF_VERSION] = prevIefVersion;
        params[paramConstants.PREV_PROCESS_VERSION] = prevProcessVersion;
      }

      const jobLabels = jobLabelsByJobId[jobId];
      const jobLabel = jobLabels && jobLabels.length > 0 ? jobLabels[0] : undefined;

      const buildIds = buildIdsByJobId[jobId];
      let selectedBuildId = undefined;

      for (const id in buildIds)
      {
        if (buildsById.hasOwnProperty(buildIds[id]))
        {
          selectedBuildId = buildIds[id];
          break;
        }
      }
      const build = buildsById[selectedBuildId];
      // if build is falsy then visuals may be missing
      // can't warn user at the moment, toastr in a selector blows up
      const isProd = build && buildIds && buildIds.length > 0 ?
        build.is_prod :
        false;

      const isIefDisabled =
        !isProd &&
        !params[paramConstants.PREV_IEF_VERSION] &&
        !params[paramConstants.IEF_ENGINE_VERSION];

      const isAxiomDisabled =
        !isProd &&
        !params[paramConstants.PREV_PROCESS_VERSION] &&
        !params[paramConstants.PROCESS_VERSION];


      const scenario = scenariosByRunner[jobId] && scenariosByRunner[jobId][0];
      const siblingScenarios = scenario &&
        scenariosByDispatcherId[scenario.dispatcher] ||
        [];
      const dispatcher = scenario && dispatchersById[scenario.dispatcher];
      const dispatcherParams = dispatcher && paramsByIds[dispatcher.job_id] || {};

      // job names might be under folders, if still on prem then we need backslashes for path building
      let jobName = params[paramConstants.JENKINS_JOB_NAME] || '';
      if (!utils.stringToBool(dispatcherParams[paramConstants.JENKINS_USE_S3]))
      {
        jobName = jobName.replace('/', '\\');
      }
      const jobNumber = params[paramConstants.JENKINS_BUILD_NUMBER] || '';
      const image = params[paramConstants.JENKINS_IMAGE_FILENAME] || params[paramConstants.IMAGEFILE] || '';

      const hasEnoughJenkinsInfo = actionItemHelpers.hasEnoughJenkinsInfo(
        params[paramConstants.JENKINS_BOT_SERVER_NAME],
        jobName,
        jobNumber
      );

      const actions = {
        [actionTypeHelpers.BREAK]: [{ isSeparator: true }],
        [actionTypeHelpers.OPEN_CHILD_JOB_URL]:
          actionItemHelpers._getChildJobOpenUrlActions({
            jobRelationshipsByParentJobId,
            paramsByJobId: paramsByIds,
            parentJobId: jobId,
          }),

        [actionTypeHelpers.RETRIGGER]: [{
          title: actionTypeHelpers.RETRIGGER,
          onSelect: actionItemHelpers._startJob(jobId)
        }],
        [actionTypeHelpers.OPEN_DIFF_VIEWER]: [
          {
            title: actionTypeHelpers.OPEN_DIFF_VIEWER_C2C,
            onSelect: actionItemHelpers._openCaseMetricsDiffViewer(
              'c2c',
              image,
              toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
              jobName,
              jobNumber,
              dispatcherParams
            ),
            isDisabled: !hasCcfDiff || !hasEnoughJenkinsInfo
          },
          {
            title: actionTypeHelpers.OPEN_DIFF_VIEWER_N2N,
            onSelect: actionItemHelpers._openCaseMetricsDiffViewer(
              'n2n',
              image,
              toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
              jobName,
              jobNumber,
              dispatcherParams
            ),
            isDisabled: !hasNcfDiff || !hasEnoughJenkinsInfo
          }
        ],
        [actionTypeHelpers.RUN_WHATS_IN_JOB]: [
          {
            title: actionTypeHelpers.RUN_WHATS_IN_JOB_IEF,
            onSelect: actionItemHelpers._whatsInClick(params, 'ief'),
            isDisabled: isIefDisabled
          },
          {
            title: actionTypeHelpers.RUN_WHATS_IN_JOB_AXIOM,
            onSelect: actionItemHelpers._whatsInClick(params, 'axiom'),
            isDisabled: isAxiomDisabled
          }
        ],
        [actionTypeHelpers.LABEL_AS_OUTLIER]: [
          {
            title: jobLabel ? actionTypeHelpers.REMOVE_AS_OUTLIER :
              actionTypeHelpers.LABEL_AS_OUTLIER,
            onSelect: jobLabel ? removeOutlierClick(jobId) : addOutlierClick(jobId)
          }
        ],
        [actionTypeHelpers.COPY_METRICS_FILE_PATH] : [
          {
            title: actionTypeHelpers.COPY_METRICS_FILE_PATH,
            onSelect: actionItemHelpers._copyMetricsFilePath(
              toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
              jobNumber,
              dispatcherParams
            )
          }
        ],
        [actionTypeHelpers.OPEN_S3_PATH]: [{
          title: actionTypeHelpers.OPEN_S3_PATH,
          isDisabled:  !utils.stringToBool(dispatcherParams[paramConstants.JENKINS_USE_S3]),
          onSelect: actionItemHelpers._openS3Path(
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber,
            image,
            '/case'
          )
        }],
        [actionTypeHelpers.COPY_PERFORMANCE_METRICS_FILE_PATH]: [
          {
            title: actionTypeHelpers.COPY_PERFORMANCE_METRICS_FILE_PATH,
            onSelect: actionItemHelpers._copyPerformanceMetricsFilePath(
              toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
              jobName,
              jobNumber,
              dispatcherParams
            ),
            isDisabled: !hasEnoughJenkinsInfo
          }
        ],
        [actionTypeHelpers.COPY_DIFF_RESULT_PATH]: [ {
          title: actionTypeHelpers.COPY_C2C_DIFF_RESULT_PATH,
          onSelect: actionItemHelpers._copyCaseMetricsDiffFilePath(
            'c2c',
            image,
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber,
            dispatcherParams
          ),
          isDisabled: !hasCcfDiff || !hasEnoughJenkinsInfo
        },
        {
          title: actionTypeHelpers.COPY_N2N_DIFF_RESULT_PATH,
          onSelect: actionItemHelpers._copyCaseMetricsDiffFilePath(
            'n2n',
            image,
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber,
            dispatcherParams
          ),
          isDisabled: !hasNcfDiff || !hasEnoughJenkinsInfo
        },
        {
          title: actionTypeHelpers.OPEN_N2N_DIFF_RESULT_S3_PATH,
          onSelect: actionItemHelpers._openCaseMetricsDiffS3Path(
            image,
            toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
            jobName,
            jobNumber
          ),
          isDisabled: !hasNcfDiff || !hasEnoughJenkinsInfo ||
            !utils.stringToBool(dispatcherParams[paramConstants.JENKINS_USE_S3])
        }],
        [actionTypeHelpers.COPY_FILE_PATH]: [
          {
            title: actionTypeHelpers.COPY_FILE_PATH,
            onSelect: actionItemHelpers._copyCaseMetricsCaseFilePath(
              image,
              toLower(params[paramConstants.JENKINS_BOT_SERVER_NAME]),
              jobName,
              jobNumber,
              dispatcherParams
            ),
            isDisabled: !hasEnoughJenkinsInfo
          }
        ],
        [actionTypeHelpers.OPEN_JOB_URL]: [{
          title: actionTypeHelpers.OPEN_JOB_URL,
          onSelect: actionItemHelpers._openScenarioJobUrl(
            jobsById[jobId],
            paramsByIds,
            siblingScenarios
          )
        }],
      };

      actionNames.forEach((actionName) => {
        finalActions.push(actions[actionName]);
      });
      return finalActions;
    }
);

/**
 * Checks for scenario fail conditions based on child job diff results
 * that may overturn the scenario status pulled from the backend
 */
export const getScenarioChildShowsFailures = createCachedSelector(
  (state, runnerJobId) =>
    jobRelationshipReducer.getJobRelationshipsByParentJobId(state, runnerJobId) || [],
  (state) => metricsReducer.metricsByJobIds(state) || {},
  (relationships, metricsByJobIds) => {
    const result = relationships.reduce((childResult, relationship) => {
      // every child job from relationships
      const childMetrics = metricsByJobIds[relationship.child_job_id] || {};
      const statusInfo = Object.keys(childMetrics).reduce((metricResult, metricKey) => {
        // every child job metric from child job
        const lowerKey = metricKey.toLowerCase();
        const lowerDiffResultSuffix =
          metricsHelpers.RUN_CASE_METRICS_DIFF_RESULT_SUFFIX.toLowerCase();
        const lowerStatusCodeSuffix =
          metricsHelpers.RUN_CASE_METRICS_DIFF_STATUS_CODE_SUFFIX.toLowerCase();

        // check for diff fails or errors
        const diffResult = childMetrics[metricKey];
        if (!lowerKey.endsWith(lowerDiffResultSuffix)) {
          if (lowerKey.endsWith(lowerStatusCodeSuffix)) {
            metricResult['result'] = metricResult.result || diffResult !== 'OK';
            if (diffResult === 'WARN') {
              metricResult['message'] = 'Child diff contains failures';
            }
            else if (diffResult === 'ERROR') {
              metricResult['message'] = 'Child diff contains errors';
            }
            return metricResult;
          }
          return metricResult;
        }
        metricResult.result = metricResult.result || diffResult.toLowerCase() === 'fail';
        return metricResult;
      }, { 'result' : false });

      // compare the results - once childResult.result = true it should stay true
      childResult.result = childResult.result || statusInfo.result;
      if (statusInfo.message) childResult['message'] = statusInfo.message;
      return childResult;
    }, { 'result': false });
    return result;
  }
)(
  (_state, runnerJobId) => runnerJobId || 0
);

/**
 * Helps override Case/Engine metric scenario statuses when the runner
 * hits certain conditions that cause false statuses to be stored.
 */
export const getAnalyzedMetricRunnerStatus = createCachedSelector(
  (state, runnerJobId) => getScenarioChildShowsFailures(state, runnerJobId),
  (state, runnerJobId) => paramsReducer.getParamsByJobId(state, runnerJobId),
  (state, runnerJobId) => scenarioReducer.getScenarioByRunner(state, runnerJobId),
  (hasChildDiffs, jobParams, scenario) => {
    const statusReturn = scenario && scenario.status; // parent scenario status
    const paramsShowError = jobParams && jobParams[paramConstants.ERROR_CODE];
    const scenarioInfo = {};

    // determine level of override and return
    if (paramsShowError) {
      scenarioInfo['status'] = scenarioHelpers.SCENARIO_ERROR;
      return scenarioInfo;
    } else if (hasChildDiffs.result) {
      scenarioInfo['status'] = scenarioHelpers.SCENARIO_FAILED;
      // determine verdict message (and override for parent scenario messages)
      if (statusReturn === scenarioHelpers.SCENARIO_ERROR)
      {
        scenarioInfo['message'] = 'Parent scenario encountered error';
        // if parent scenario is an error then set verdict as error too
        scenarioInfo['status'] = statusReturn;
      }
      else if (statusReturn === scenarioHelpers.SCENARIO_FAILED)
      {
        scenarioInfo['message'] = 'Parent scenario failed';
      }
      else {
        scenarioInfo['message'] = hasChildDiffs.message;
      }
      return scenarioInfo;
    } else {
      scenarioInfo['status'] = statusReturn;
      // determine verdict message
      if (statusReturn === scenarioHelpers.SCENARIO_COMPLETED)
      {
        scenarioInfo['message'] = 'All passed';
      }
      else if (statusReturn === scenarioHelpers.SCENARIO_ERROR)
      {
        scenarioInfo['message'] = 'Parent scenario encountered error';
      }
      else if (statusReturn === scenarioHelpers.SCENARIO_FAILED)
      {
        scenarioInfo['message'] = 'Parent scenario failed';
      }
      return scenarioInfo;
    }
  }
)(
  (_state, runnerJobId) => runnerJobId || 0
);


// WEBPACK FOOTER //
// src/selectors/metricsPage.js